import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RawTableIndexCell, RawTableIndexHead } from '@/components/ui/table-index';
import { rowSerial } from '@/lib/row-serial';
import { Pagination } from '@/components/ui/pagination';
import { useClientPagination } from '@/hooks/useClientPagination';
import { ConfirmActionDialog } from '@/components/ui/confirm-action-dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { formatDeleteError } from '@/lib/api';

export default function UnitsListPage() {
  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ name: '', symbol: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [toggling, setToggling] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ item: any; action: 'activate' | 'deactivate' } | null>(null);
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const canDelete = hasPermission('units:delete');
  const { page, setPage, pageSize, total, pageItems } = useClientPagination(units, {
    resetKey: statusFilter,
  });

  useEffect(() => { loadUnits(); }, [statusFilter]);

  async function loadUnits() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('limit', '1000');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get<{ data: any[] }>(`/units${qs}`);
      setUnits(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handleSave() {
    if (!form.name.trim() || !form.symbol.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (editing) { await api.put(`/units/${editing.id}`, { name: form.name.trim(), symbol: form.symbol.trim() }); }
      else { await api.post('/units', { name: form.name.trim(), symbol: form.symbol.trim() }); }
      setForm({ name: '', symbol: '' }); setEditing(null); addToast(editing ? 'Updated' : 'Created', 'success'); await loadUnits();
    } catch (e: any) { setError(e?.message || 'Failed'); }
    setSaving(false);
  }

  async function handleToggleStatus(item: any) {
    setToggling(item.id);
    try {
      await api.put(`/units/${item.id}`, { isActive: !item.is_active });
      addToast(item.is_active ? `${item.name} deactivated successfully.` : `${item.name} activated successfully.`, 'success');
      await loadUnits();
    } catch (e: any) { addToast(e?.message || 'Failed', 'error'); }
    setToggling(null);
  }

  const handleDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/units/${deleteTarget.id}`);
      addToast('Unit permanently deleted.', 'success');
      setDeleteTarget(null);
      await loadUnits();
    } catch (e: unknown) { addToast(formatDeleteError(e, 'unit'), 'error'); }
    setDeleting(false);
  }, [deleteTarget, deleting]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">Units of Measurement</h1>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <div className="flex-1 space-y-1"><label className="text-sm font-medium">Name</label><Input placeholder="e.g. Running Feet" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="w-full sm:w-32 space-y-1"><label className="text-sm font-medium">Symbol</label><Input placeholder="RF" value={form.symbol} onChange={e => setForm(f => ({ ...f, symbol: e.target.value }))} /></div>
            <Button onClick={handleSave} disabled={saving || !form.name.trim() || !form.symbol.trim()}>{editing ? 'Update' : <><Plus size={16} className="mr-1" />Add</>}</Button>
            {editing && <Button variant="ghost" onClick={() => { setEditing(null); setForm({ name: '', symbol: '' }); }}>Cancel</Button>}
          </div>
          {error && <p className="text-sm text-error mt-2">{error}</p>}
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {(['all', 'active', 'inactive'] as const).map(v => (
              <Button key={v} type="button" size="sm" variant={statusFilter === v ? 'default' : 'outline'} onClick={() => setStatusFilter(v)}>
                {v === 'all' ? 'All' : v === 'active' ? 'Active' : 'Inactive'}
              </Button>
            ))}
          </div>
          {loading ? <p className="text-center py-8 text-secondary">Loading...</p> : units.length === 0 ? (
            <p className="text-center py-8 text-secondary">No units found</p>
          ) : (
            <>
            <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-0 text-sm">
              <thead><tr className="border-b"><RawTableIndexHead /><th className="text-left py-3">Name</th><th className="text-left py-3">Symbol</th><th className="text-left py-3">Status</th><th className="text-right py-3">Actions</th></tr></thead>
              <tbody>{pageItems.map((u: any, index: number) => (
                <tr key={u.id} className="group border-b hover:bg-hover transition-colors">
                  <RawTableIndexCell value={rowSerial(index, page, pageSize)} />
                  <td className="py-3 font-medium">{u.name}</td>
                  <td className="py-3">{u.symbol}</td>
                  <td className="py-3"><Badge variant={u.is_active ? 'approved' : 'inactive'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></td>
                  <td className="py-3 text-right space-x-1">
                    {u.is_active ? (
                      <Button variant="ghost" size="sm" className="text-warning hover:text-warning" disabled={toggling === u.id} onClick={() => setConfirmTarget({ item: u, action: 'deactivate' })}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-success hover:text-success" disabled={toggling === u.id} onClick={() => setConfirmTarget({ item: u, action: 'activate' })}>
                        Activate
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(u); setForm({ name: u.name, symbol: u.symbol }); }} aria-label={`Edit ${u.name}`} title="Edit"><Pencil size={14} /></Button>
                    {canDelete && (
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteTarget(u)} aria-label={`Permanently delete ${u.name}`} title="Permanently delete"><Trash2 size={14} /></Button>
                    )}
                  </td>
                </tr>
              ))}</tbody>
            </table>
            </div>
            <div className="space-y-3 md:hidden">
              {pageItems.map((u: any) => (
                <div key={u.id} className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
                  <p className="font-medium text-primary break-words">{u.name}</p>
                  <dl className="text-sm space-y-1">
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Symbol</dt><dd className="text-primary text-right break-words">{u.symbol}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Status</dt><dd><Badge variant={u.is_active ? 'approved' : 'inactive'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></dd></div>
                  </dl>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {u.is_active ? (
                      <Button variant="ghost" size="sm" className="text-warning hover:text-warning min-h-[44px]" disabled={toggling === u.id} onClick={() => setConfirmTarget({ item: u, action: 'deactivate' })}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-success hover:text-success min-h-[44px]" disabled={toggling === u.id} onClick={() => setConfirmTarget({ item: u, action: 'activate' })}>
                        Activate
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={() => { setEditing(u); setForm({ name: u.name, symbol: u.symbol }); }} aria-label={`Edit ${u.name}`}>Edit</Button>
                    {canDelete && (
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 min-h-[44px]" onClick={() => setDeleteTarget(u)} aria-label={`Permanently delete ${u.name}`}>Delete</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete Unit?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete <strong>{deleteTarget?.name}</strong> ({deleteTarget?.symbol}). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete}>
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmActionDialog
        open={!!confirmTarget && confirmTarget.action === 'deactivate'}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Deactivate ${confirmTarget?.item?.name}?`}
        description="This record will remain in existing records and reports, but it will not be available for new selections."
        actionLabel="Deactivate"
        buttonStyle="amber"
        loading={!!confirmTarget && toggling === confirmTarget.item?.id}
        loadingLabel="Deactivating..."
        onConfirm={() => { const t = confirmTarget; setConfirmTarget(null); if (t) handleToggleStatus(t.item); }}
      />

      <ConfirmActionDialog
        open={!!confirmTarget && confirmTarget.action === 'activate'}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Activate ${confirmTarget?.item?.name}?`}
        description="This record will become available for new selections."
        actionLabel="Activate"
        buttonStyle="green"
        loading={!!confirmTarget && toggling === confirmTarget.item?.id}
        loadingLabel="Activating..."
        onConfirm={() => { const t = confirmTarget; setConfirmTarget(null); if (t) handleToggleStatus(t.item); }}
      />
    </div>
  );
}
